import { ActionContext } from 'vuex';
import { Profile } from '../../../scripts/api/models/user';
import UserList from '../room-members/UserList';

///
// State

type PromiseResolver = (profiles: Profile[]) => void;

export type State = {
    userCountByGroup: null | Map<string, number>,

    roomUid: null | number,

    usersByGroup: Map<string, UserList>

    loadedKeys: string[],

    loadingKeys: string[],

    // keys whose last load attempt failed; they can be retried
    failedKeys: string[],

    loadingPromises: Map<string, Promise<Profile[]>>

    // the functions that are used to resolve the Promises above
    loadingPromiseResolvers: Map<string, PromiseResolver>
};

///
// Mutations

export enum MutationTypes {
    SET_ROOM_UID = 'SET_ROOM_UID',

    INITIALIZE_GROUPS = 'INITIALIZE_GROUPS',

    START_LOADING = 'START_LOADING',

    SET_USERS = 'SET_USERS',

    LOADING_FAILED = 'LOADING_FAILED',

    FINISH_LOADING = 'FINISH_LOADING',

    DELETE_USER = 'DELETE_USER',
}

type MutationsWithoutParameter<S = State> = {
}
type MutationsWithOneParameter<S = State> = {
    [MutationTypes.SET_ROOM_UID](state: S, payload: number): void
    [MutationTypes.INITIALIZE_GROUPS](state: S, data: Map<string, number>): void
    [MutationTypes.START_LOADING](state: S, key: string): void
    [MutationTypes.SET_USERS](state: S, payload: { key: string, users: Profile[] }): void
    [MutationTypes.LOADING_FAILED](state: S, keys: string[]): void
    [MutationTypes.FINISH_LOADING](state: S, key: string): void
    [MutationTypes.DELETE_USER](state: S, user: number): void
}
// TODO check if this can be done easier (we need this to have correct typing in the commit() calls with different parameter sets)
export type Mutations<S = State> = MutationsWithoutParameter & MutationsWithOneParameter;

///
// Actions

export type AugmentedActionContext = {
    commit<K extends keyof MutationsWithoutParameter>(
        key: K
    ): ReturnType<MutationsWithoutParameter[K]>
} & {
    commit<K extends keyof MutationsWithOneParameter>(
        key: K,
        payload: Parameters<MutationsWithOneParameter[K]>[1]
    ): ReturnType<MutationsWithOneParameter[K]>
} & Omit<ActionContext<State, State>, 'commit'>

export interface Actions {
    setRoomUid({ commit } : AugmentedActionContext, roomUid: number): void

    setGroups ({ commit } : AugmentedActionContext, groups: { [key: string]: number }): void

    fetchUsers ({ commit }: AugmentedActionContext, key: string): Promise<Profile[]>

    removeUser ({ commit }: AugmentedActionContext, user: number): Promise<void>
}

///
// Getters

export interface Getters {
    groupsAvailable(state: State): boolean;

    groupKeys(state: State): Iterable<string>;

    userListLoaded(state: State): (key: string) => boolean;

    userListFailed(state: State): (key: string) => boolean;

    // deprecated
    groupsAndCount (state: State): null | Map<string, number>

    users (state: State): Map<string, UserList>
}
